import React, { useState } from "react";
import axios from "axios";
import CelticOrnament from "@/components/CelticOrnament";
import AlbetheineEye from "@/components/AlbetheineEye";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  { id: "app", label: "Albetheine Oracle App" },
  { id: "guidebook", label: "Online Guidebook" },
  { id: "bundle", label: "Guidebook + App Bundle" },
  { id: "beacon", label: "Beacon Oracle Deck" },
  { id: "shadow", label: "Shadow Oracle Deck" },
  { id: "readings", label: "Psychic Readings" },
];

export default function FoundersCircle() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interests, setInterests] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const toggleInterest = (id) => {
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/founders-circle`, {
        name: name.trim() || null,
        email: email.trim(),
        interests,
      });
      setStatus("success");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went quiet on our end. Please try again.";
      setErrorMsg(typeof detail === "string" ? detail : "Please check your email and try again.");
      setStatus("error");
    }
  };

  return (
    <section
      id="founders-circle"
      className="relative bg-charcoal py-24 md:py-32 overflow-hidden border-t border-[rgba(201,169,97,0.18)]"
      data-testid="founders-circle-section"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center">
          <AlbetheineEye size={70} className="mx-auto opacity-90" />
          <div className="eyebrow mt-6">FOUNDER&apos;S CIRCLE</div>
          <h2 className="section-heading text-3xl md:text-5xl mt-5 leading-tight">
            Be the first to{" "}
            <span className="gold-gradient-text">light the candle.</span>
          </h2>
          <CelticOrnament size={70} className="mx-auto mt-5 opacity-70" />
          <p className="text-cream-soft mt-7 leading-relaxed max-w-2xl mx-auto">
            Join Albetheine&apos;s Founder&apos;s Circle and you&apos;ll be
            first to know when the <strong className="text-cream">Beacon</strong>{" "}
            and <strong className="text-cream">Shadow</strong> oracle decks open
            for order &mdash; plus a private{" "}
            <strong className="text-gold-bright">£20 launch-day discount</strong>{" "}
            on the Guidebook + 3 Months App bundle.
          </p>
        </div>

        {status === "success" ? (
          <div
            className="mt-12 card-occult relative p-8 md:p-10 text-center"
            data-testid="founders-success"
          >
            <span className="corner-ornament corner-tl" />
            <span className="corner-ornament corner-tr" />
            <span className="corner-ornament corner-bl" />
            <span className="corner-ornament corner-br" />
            <div className="font-cinzel text-gold-bright text-[0.78rem] tracking-[0.32em]">
              · WELCOME, KEEPER OF THE FLAME ·
            </div>
            <h3 className="font-cinzel text-cream text-2xl md:text-3xl mt-5 leading-snug">
              You&apos;re in.
            </h3>
            <p className="text-cream-soft mt-5 leading-relaxed">
              We&apos;ll send a single, considered note when the decks open and
              when your £20 launch-day discount is live. No noise. No spam.
              Just the candle being lit.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-12 card-occult relative p-8 md:p-10"
            data-testid="founders-form"
            noValidate
          >
            <span className="corner-ornament corner-tl" />
            <span className="corner-ornament corner-tr" />
            <span className="corner-ornament corner-bl" />
            <span className="corner-ornament corner-br" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="font-cinzel text-gold text-[0.7rem] tracking-[0.28em]">
                  YOUR NAME (OPTIONAL)
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What shall we call you?"
                  className="mt-2 w-full bg-charcoal border border-[rgba(201,169,97,0.35)] text-cream px-4 py-3 placeholder:text-cream-muted focus:outline-none focus:border-[var(--gold)] transition-colors"
                  data-testid="founders-name-input"
                />
              </label>
              <label className="block">
                <span className="font-cinzel text-gold text-[0.7rem] tracking-[0.28em]">
                  YOUR EMAIL
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@candlelit.com"
                  className="mt-2 w-full bg-charcoal border border-[rgba(201,169,97,0.35)] text-cream px-4 py-3 placeholder:text-cream-muted focus:outline-none focus:border-[var(--gold)] transition-colors"
                  data-testid="founders-email-input"
                />
              </label>
            </div>

            <div className="mt-7">
              <div className="font-cinzel text-gold text-[0.7rem] tracking-[0.28em] mb-3">
                WHAT CALLS TO YOU? (OPTIONAL)
              </div>
              <div className="flex flex-wrap gap-2">
                {INTEREST_OPTIONS.map((opt) => {
                  const active = interests.includes(opt.id);
                  return (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => toggleInterest(opt.id)}
                      className={`font-cinzel text-[0.7rem] tracking-[0.18em] px-4 py-2 border transition-all ${
                        active
                          ? "bg-[rgba(201,169,97,0.18)] border-[var(--gold)] text-cream"
                          : "border-[rgba(201,169,97,0.35)] text-cream-soft hover:border-[var(--gold)] hover:text-cream"
                      }`}
                      data-testid={`founders-interest-${opt.id}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {status === "error" && (
              <div
                className="mt-6 text-sm font-cinzel tracking-[0.18em] text-[#e89c7a]"
                data-testid="founders-error"
              >
                · {errorMsg} ·
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-cream-muted text-xs font-cinzel tracking-[0.22em]">
                · ONE EMAIL, MAYBE TWO · UNSUBSCRIBE ANYTIME ·
              </p>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="btn-gold disabled:opacity-60 disabled:cursor-wait"
                data-testid="founders-submit"
              >
                {status === "submitting" ? "Sealing the letter…" : "Join the Circle"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
